/**
 * Lightweight flashbar context.
 *
 * Cloudscape's `Flashbar` accepts a list of `FlashbarProps.MessageDefinition`
 * objects. The AgentMesh shell renders a single Flashbar at the top of
 * the AppLayout `notifications` slot; this context gives any component
 * the ability to push, dismiss, and clear messages without prop-drilling.
 *
 * Use cases today:
 *   - Policy denials (was `<PolicyDenialBanner>`) → push an `error` flash
 *     with the denied action and the policy id.
 *   - Action toasts (Approve / Deprecate result) → push a `success` flash.
 *   - Form errors → push an `error` flash.
 *
 * Honest_Cost_Banner stays inline above each cost tile (it is a load-
 * bearing always-visible disclaimer, not a transient toast — see R8.5).
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { FlashbarProps } from "@cloudscape-design/components";

export type FlashMessage = FlashbarProps.MessageDefinition & { id: string };

interface FlashbarContextValue {
  /** Live list of messages. The shell binds this to <Flashbar items={...} />. */
  items: FlashMessage[];
  /** Push a new message and return its assigned id. */
  push: (
    message: Omit<FlashbarProps.MessageDefinition, "id" | "onDismiss"> & {
      id?: string;
    },
  ) => string;
  /** Dismiss a message by id (the Flashbar's dismiss button calls this). */
  dismiss: (id: string) => void;
  /** Remove every message currently on the flashbar. */
  clear: () => void;
}

const FlashbarContext = createContext<FlashbarContextValue | null>(null);

interface FlashbarProviderProps {
  children: ReactNode;
}

export function FlashbarProvider({ children }: FlashbarProviderProps) {
  const [items, setItems] = useState<FlashMessage[]>([]);
  const counterRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const push = useCallback(
    (
      message: Omit<FlashbarProps.MessageDefinition, "id" | "onDismiss"> & {
        id?: string;
      },
    ): string => {
      counterRef.current += 1;
      const id = message.id ?? `flash-${counterRef.current}`;
      setItems((prev) => [
        ...prev,
        {
          ...message,
          id,
          dismissible: message.dismissible ?? true,
          onDismiss: () => dismiss(id),
        },
      ]);
      return id;
    },
    [dismiss],
  );

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo<FlashbarContextValue>(
    () => ({ items, push, dismiss, clear }),
    [items, push, dismiss, clear],
  );

  return (
    <FlashbarContext.Provider value={value}>{children}</FlashbarContext.Provider>
  );
}

/** Hook for callers that want to push / dismiss flash messages. */
export function useFlashbar(): FlashbarContextValue {
  const ctx = useContext(FlashbarContext);
  if (!ctx) {
    throw new Error("useFlashbar must be used within <FlashbarProvider>");
  }
  return ctx;
}
